/**
 * DeleteCollectionModal — hard-delete confirm. The user must type the
 * collection name exactly to enable the destructive button. Modal copy
 * explicitly calls out that channel datasets, splits, and upload
 * history are all destroyed (no soft-delete recovery).
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '../../../../../ui/alert';
import { Button } from '../../../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';
import { Input } from '../../../../../ui/input';
import { Label } from '../../../../../ui/label';

import { useDeleteCollection } from '../../hooks/useDatasets';
import type { Collection } from '../../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collection: Collection;
  onDeleted: () => void;
}

const DeleteCollectionModal: React.FC<Props> = ({
  open,
  onOpenChange,
  collection,
  onDeleted,
}) => {
  const deleteCollection = useDeleteCollection();
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (open) setConfirmText('');
  }, [open]);

  // Trim both sides so a collection whose name accidentally carries
  // leading/trailing whitespace from a past data-entry quirk can still
  // be confirmed. The BE trims at create/update, so this is just a
  // belt-and-braces guard.
  const enabled = confirmText.trim() === collection.name.trim();

  const handleDelete = () => {
    if (!enabled) return;
    deleteCollection.mutate(collection.id, {
      onSuccess: () => {
        onOpenChange(false);
        onDeleted();
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Delete collection</DialogTitle>
          <DialogDescription>
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Alert variant="destructive" data-testid="delete-collection-warning">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>All data will be lost</AlertTitle>
            <AlertDescription>
              This will permanently delete the collection, all of its channel
              datasets, every split, and the full upload history. There is no
              way to recover this data from the UI.
            </AlertDescription>
          </Alert>

          <div>
            <Label htmlFor="delete-confirm-input">
              Type <span className="font-semibold">{collection.name}</span> to confirm
            </Label>
            <Input
              id="delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="mt-1"
              autoComplete="off"
              data-testid="delete-confirm-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteCollection.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!enabled || deleteCollection.isPending}
            data-testid="delete-confirm-button"
          >
            {deleteCollection.isPending ? 'Deleting…' : 'Delete collection'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteCollectionModal;
